/**
 * BrandingSettingsPanel — edits a Company's brand (ADR-023): the signing
 * experience and email sign-off for every workspace the Company links.
 *
 * Shown inline on Company → Branding, the only place a brand is edited — there
 * is no per-workspace brand. It lives beside Document Sign's signing components
 * because its live preview IS the signing page (`BrandedSigningFrame`), so the
 * preview cannot drift from what recipients see. Server-stored, because the
 * people who see it are RECIPIENTS on their own devices.
 *
 * Branding is a fixed set of tokens — name, mark, two colours, a closing
 * message — rather than uploaded markup or CSS. The signing page is served on
 * the platform origin, outside any iframe sandbox, to recipients who may be
 * signed in to Fias, so sender-supplied markup there would be a
 * cross-site-scripting vector against other customers' sessions. In practice
 * nothing is lost: a brand guide already is a token system.
 *
 * # No brand presets
 *
 * This dialog used to offer a "Start from:" row of named brands. That shipped a
 * third party's mark in the bundle served to every user and presented it as a
 * one-click starting point — the shape of a directory of brands to impersonate,
 * which stays wrong even now that only approved Companies reach this form. A
 * partner's brand is set up once, as data, and edited here; it is never offered
 * to anyone else as a template.
 */

import { useEffect, useId, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import api, { ApiError, type DocumentSignPublicBranding } from '@/lib/api';
import { queryKeys } from '@/lib/query-keys';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Image as ImageIcon } from 'lucide-react';
import { fileToBrandLogoDataUrl, BrandLogoError } from '../brandLogo';
import { isLegibleHeaderTextColor, onColor, BRAND_MIN_CONTRAST_RATIO } from '../brandColor';
import { BrandedSigningFrame, BrandedCompletionMessage } from './BrandedSigningFrame';

const HEX_RE = /^#[0-9a-fA-F]{6}$/;

type BrandingInput = Required<DocumentSignPublicBranding>;

/** The saved brand in the form's terms, so "unsaved changes" is a plain comparison. */
function savedInput(b: DocumentSignPublicBranding | null | undefined): BrandingInput {
  return {
    displayName: b?.displayName ?? null,
    logoDataUrl: b?.logoDataUrl ?? null,
    primaryColor: b?.primaryColor ?? null,
    accentColor: b?.accentColor ?? null,
    headerTextColor: b?.headerTextColor ?? null,
    completionMessage: b?.completionMessage ?? null,
    completionCtaLabel: b?.completionCtaLabel ?? null,
    completionCtaUrl: b?.completionCtaUrl ?? null,
  };
}

export function BrandingSettingsPanel({ companyId }: { companyId: string }) {
  const qc = useQueryClient();
  const queryKey = queryKeys.companies.branding(companyId);
  const existing = useQuery({
    queryKey,
    queryFn: () => api.getCompanyBranding(companyId),
  });
  const saved = existing.data?.branding ?? null;

  const [displayName, setDisplayName] = useState('');
  const [logoDataUrl, setLogoDataUrl] = useState('');
  const [primaryColor, setPrimaryColor] = useState('');
  const [accentColor, setAccentColor] = useState('');
  const [headerTextColor, setHeaderTextColor] = useState('');
  const [completionMessage, setCompletionMessage] = useState('');
  const [ctaLabel, setCtaLabel] = useState('');
  const [ctaUrl, setCtaUrl] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [logoBusy, setLogoBusy] = useState(false);
  const [confirmingClear, setConfirmingClear] = useState(false);
  const [justSaved, setJustSaved] = useState(false);
  // The saved brand the form was last loaded from — the baseline "unsaved
  // changes" is measured against. Null until the first load. Deliberately NOT
  // the live query data: the Company page refetches on window focus, and a
  // brand changed elsewhere must neither wipe an edit in progress nor turn an
  // untouched form into a stale one that a save would write back.
  const [baseline, setBaseline] = useState<BrandingInput | null>(null);
  // The form as last rendered, for a write's success handler to compare with
  // what it submitted.
  const latestForm = useRef<BrandingInput | null>(null);
  const [logoError, setLogoError] = useState<string | null>(null);
  const [logoNote, setLogoNote] = useState<string | null>(null);
  const logoInputRef = useRef<HTMLInputElement>(null);
  const logoInputId = useId();

  const handleLogoFile = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    // Clear the input straight away so picking the same file twice still
    // fires a change event — otherwise a retry after an error does nothing.
    e.target.value = '';
    if (!file) return;

    setLogoError(null);
    setLogoNote(null);
    setLogoBusy(true);
    try {
      const dataUrl = await fileToBrandLogoDataUrl(file);
      setLogoDataUrl(dataUrl);
      // Say so when the file was altered. Silently resizing someone's logo and
      // letting them discover it on a signed contract would be worse than the
      // extra line.
      if (file.type === 'image/png' && dataUrl.length < file.size) {
        setLogoNote('Resized to fit the logo size limit.');
      }
    } catch (err) {
      setLogoError(
        err instanceof BrandLogoError ? err.message : 'That file could not be used as a logo',
      );
    } finally {
      setLogoBusy(false);
    }
  };

  const seedFrom = (b: DocumentSignPublicBranding | null) => {
    setDisplayName(b?.displayName ?? '');
    setLogoDataUrl(b?.logoDataUrl ?? '');
    setPrimaryColor(b?.primaryColor ?? '');
    setAccentColor(b?.accentColor ?? '');
    setHeaderTextColor(b?.headerTextColor ?? '');
    setCompletionMessage(b?.completionMessage ?? '');
    setCtaLabel(b?.completionCtaLabel ?? '');
    setCtaUrl(b?.completionCtaUrl ?? '');
    setError(null);
    setConfirmingClear(false);
    setBaseline(savedInput(b));
  };

  // Seed the form from the server, so an edit starts from what recipients are
  // currently seeing rather than from blank — and follow the server while the
  // form is untouched. Once the admin has edited, their edit wins; a newer
  // brand from elsewhere is flagged next to Save instead (see changedElsewhere).
  useEffect(() => {
    if (!existing.data) return;
    const incoming = savedInput(existing.data.branding);
    const pristine =
      baseline === null || JSON.stringify(latestForm.current) === JSON.stringify(baseline);
    if (pristine && JSON.stringify(incoming) !== JSON.stringify(baseline)) {
      seedFrom(existing.data.branding);
    }
  }, [existing.data, baseline]);

  // What the write settled on becomes the baseline straight away, from the
  // response — not from a refetch, which may land after the admin has started
  // typing again.
  const afterWrite = (branding: DocumentSignPublicBranding | null) => {
    qc.setQueryData<Awaited<ReturnType<typeof api.getCompanyBranding>>>(queryKey, (old) =>
      old ? { ...old, branding } : old,
    );
    setJustSaved(true);
    void qc.invalidateQueries({ queryKey });
  };

  const save = useMutation({
    mutationFn: (input: BrandingInput) => api.putCompanyBranding(companyId, input),
    onSuccess: (res, submitted) => {
      // Take the server's version of the brand (it is what recipients will
      // see) only if nothing was typed while the save was in flight — an edit
      // made during the round trip is newer than either and must survive.
      if (JSON.stringify(latestForm.current) === JSON.stringify(submitted)) {
        seedFrom(res.branding);
      } else {
        setBaseline(savedInput(res.branding));
      }
      afterWrite(res.branding);
    },
  });

  const clear = useMutation({
    mutationFn: () => api.deleteCompanyBranding(companyId),
    onSuccess: () => {
      seedFrom(null);
      afterWrite(null);
    },
  });

  // Built from the form's live values, not from what is saved — the point is
  // to see a change before committing it. Invalid colours are dropped rather
  // than passed through, so a half-typed hex does not flash a broken preview.
  const previewBranding: DocumentSignPublicBranding = {
    displayName: displayName.trim() || null,
    logoDataUrl: logoDataUrl.trim().startsWith('data:image/') ? logoDataUrl.trim() : null,
    primaryColor: HEX_RE.test(primaryColor.trim()) ? primaryColor.trim() : null,
    accentColor: HEX_RE.test(accentColor.trim()) ? accentColor.trim() : null,
    headerTextColor: HEX_RE.test(headerTextColor.trim()) ? headerTextColor.trim() : null,
    completionMessage: completionMessage.trim() || null,
    completionCtaLabel: ctaLabel.trim() || null,
    completionCtaUrl: ctaUrl.trim() || null,
  };

  const colorProblem = (value: string) => value.length > 0 && !HEX_RE.test(value);

  /**
   * Why the header colour is the only one with a legibility rule: it is the
   * only one rendered AS TEXT on another sender-chosen colour. The primary and
   * accent are backgrounds, and their foregrounds are derived, so they cannot
   * be made unreadable by choosing badly.
   */
  const trimmedHeaderText = headerTextColor.trim();
  const trimmedPrimary = primaryColor.trim();
  const headerTextColorProblem =
    HEX_RE.test(trimmedHeaderText) &&
    HEX_RE.test(trimmedPrimary) &&
    !isLegibleHeaderTextColor(trimmedHeaderText, trimmedPrimary)
      ? `${trimmedHeaderText} is hard to read on ${trimmedPrimary} — it needs at least ` +
        `${BRAND_MIN_CONTRAST_RATIO}:1 contrast. Leave it blank and Fias will pick the most ` +
        'readable colour for your band.'
      : null;

  /** What the header WOULD use if the field is left blank. */
  const autoHeaderText = HEX_RE.test(trimmedPrimary) ? onColor(trimmedPrimary) : null;
  const ctaIncomplete =
    ctaLabel.trim().length > 0 !== ctaUrl.trim().length > 0
      ? 'A button needs both a label and a link, or neither.'
      : null;

  const formInput: BrandingInput = {
    displayName: displayName.trim() || null,
    logoDataUrl: logoDataUrl.trim() || null,
    primaryColor: primaryColor.trim() || null,
    accentColor: accentColor.trim() || null,
    headerTextColor: headerTextColor.trim() || null,
    completionMessage: completionMessage.trim() || null,
    completionCtaLabel: ctaLabel.trim() || null,
    completionCtaUrl: ctaUrl.trim() || null,
  };
  latestForm.current = formInput;
  const dirty = baseline !== null && JSON.stringify(formInput) !== JSON.stringify(baseline);
  // Someone else saved a different brand after this form was loaded. Only
  // reachable while dirty (an untouched form follows the server), and saving
  // replaces the whole brand — so say so rather than overwrite silently.
  const changedElsewhere =
    baseline !== null && JSON.stringify(savedInput(saved)) !== JSON.stringify(baseline);
  // "Saved" only until the next edit.
  useEffect(() => {
    if (dirty) setJustSaved(false);
  }, [dirty]);

  const handleSave = () => {
    setError(null);
    if (colorProblem(primaryColor) || colorProblem(accentColor) || colorProblem(headerTextColor)) {
      setError('Colours must be hex values like #333790.');
      return;
    }
    // Checked here as well as on the server: the point of the field is to let
    // a sender choose BETWEEN legible colours, not to let them make the header
    // unreadable. Catching it before the round trip keeps the correction next
    // to the input they are looking at.
    if (headerTextColorProblem) {
      setError(headerTextColorProblem);
      return;
    }
    if (ctaIncomplete) {
      setError(ctaIncomplete);
      return;
    }
    if (ctaUrl.trim() && !ctaUrl.trim().startsWith('https://')) {
      setError('The button link must start with https://');
      return;
    }
    save.mutate(formInput, {
      onError: (err) => setError(err instanceof ApiError ? err.message : 'Could not save branding'),
    });
  };

  if (existing.isLoading) return <p className="text-sm text-muted-foreground">Loading…</p>;

  return (
    <div className="space-y-4">
      {/* Two columns so the preview stays visible while the form is edited.
            Stacked, a "live" preview sits below the fold exactly when someone
            is changing a colour at the top — which is the one moment it is
            supposed to be useful. Collapses to one column on a narrow screen,
            where the preview follows the form rather than preceding it. */}
      <div className="grid gap-6 md:grid-cols-2">
        <div className="space-y-4 order-1">
          <div className="space-y-1.5">
            <Label htmlFor="ds-brand-name">Display name</Label>
            <Input
              id="ds-brand-name"
              value={displayName}
              onChange={(e) => setDisplayName(e.target.value)}
              placeholder="Document Sign"
              maxLength={120}
            />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label htmlFor="ds-brand-primary">Primary colour</Label>
              <Input
                id="ds-brand-primary"
                value={primaryColor}
                onChange={(e) => setPrimaryColor(e.target.value)}
                placeholder="#333790"
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="ds-brand-accent">Accent colour</Label>
              <Input
                id="ds-brand-accent"
                value={accentColor}
                onChange={(e) => setAccentColor(e.target.value)}
                placeholder="#77A7F7"
              />
            </div>
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="ds-brand-header-text">Header text colour</Label>
            <Input
              id="ds-brand-header-text"
              value={headerTextColor}
              onChange={(e) => setHeaderTextColor(e.target.value)}
              placeholder={autoHeaderText ?? '#FFFFFF'}
              aria-describedby="ds-brand-header-text-help"
              aria-invalid={headerTextColorProblem ? true : undefined}
            />
            {/* Helper text, not a placeholder: a placeholder disappears on
                  focus, which is when someone is reading it. The placeholder
                  itself shows the colour that WOULD be used, so leaving the
                  field empty is an informed choice rather than a blank. */}
            <p
              id="ds-brand-header-text-help"
              className={`text-[11px] ${headerTextColorProblem ? 'text-destructive' : 'text-muted-foreground'}`}
            >
              {headerTextColorProblem ??
                (autoHeaderText
                  ? `Applies to your name and the line beneath it. Leave blank to use ${autoHeaderText}, the most readable colour on your primary.`
                  : 'Applies to your name and the line beneath it. Leave blank to let Fias pick the most readable colour.')}
            </p>
          </div>

          <div className="space-y-2">
            <Label htmlFor={logoInputId}>Logo</Label>
            <div className="flex items-center gap-3">
              {logoDataUrl.trim().startsWith('data:image/') ? (
                <img
                  src={logoDataUrl.trim()}
                  alt=""
                  aria-hidden="true"
                  className="h-12 w-12 object-contain border rounded bg-background"
                />
              ) : (
                <div
                  className="h-12 w-12 rounded border border-dashed flex items-center justify-center text-muted-foreground"
                  aria-hidden="true"
                >
                  <ImageIcon className="h-5 w-5" />
                </div>
              )}
              <div className="flex flex-wrap gap-2">
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => logoInputRef.current?.click()}
                  disabled={logoBusy}
                >
                  {logoBusy ? 'Processing…' : logoDataUrl ? 'Replace logo' : 'Upload logo'}
                </Button>
                {logoDataUrl && (
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={() => {
                      setLogoDataUrl('');
                      setLogoNote(null);
                    }}
                    disabled={logoBusy}
                  >
                    Remove
                  </Button>
                )}
              </div>
            </div>
            <input
              ref={logoInputRef}
              id={logoInputId}
              type="file"
              accept="image/png,image/svg+xml,.png,.svg"
              className="sr-only"
              onChange={handleLogoFile}
            />
            <p className="text-xs text-muted-foreground">
              PNG or SVG. Large PNGs are resized automatically to fit the 64KB limit.
            </p>
            {logoNote && <p className="text-xs text-muted-foreground">{logoNote}</p>}
            {logoError && (
              <p className="text-sm text-destructive" role="alert">
                {logoError}
              </p>
            )}
          </div>

          <div className="space-y-1.5 pt-2 border-t">
            <Label htmlFor="ds-brand-completion">Message after signing</Label>
            <Textarea
              id="ds-brand-completion"
              value={completionMessage}
              onChange={(e) => setCompletionMessage(e.target.value)}
              placeholder="Thanks for banking with us — here's what else we can help with…"
              rows={3}
              maxLength={2000}
            />
            <p className="text-xs text-muted-foreground">
              Shown once the signer is finished. Plain text.
            </p>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label htmlFor="ds-brand-cta-label">Button label</Label>
              <Input
                id="ds-brand-cta-label"
                value={ctaLabel}
                onChange={(e) => setCtaLabel(e.target.value)}
                placeholder="Explore our services"
                maxLength={80}
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="ds-brand-cta-url">Button link</Label>
              <Input
                id="ds-brand-cta-url"
                value={ctaUrl}
                onChange={(e) => setCtaUrl(e.target.value)}
                placeholder="https://example.com"
                maxLength={500}
              />
            </div>
          </div>

          {error && (
            <p className="text-sm text-destructive" role="alert">
              {error}
            </p>
          )}
        </div>

        {/*
            Live preview. Without it the only way to see the result is to send
            a real envelope to yourself — the slowest possible feedback loop,
            and the one place a mistake is visible to a counterparty rather
            than to you.

            Rendered from the same components the signing page uses, at the
            same values, so what is shown here is what recipients get rather
            than an approximation free to drift.
          */}
        <div className="space-y-2 order-2 md:sticky md:top-4 md:self-start">
          <Label>Preview</Label>
          <div className="rounded-md border overflow-hidden">
            <BrandedSigningFrame
              fullHeight={false}
              branding={previewBranding}
              subtitle={<p className="text-xs truncate">Membership Agreement</p>}
              right={<span className="text-xs">Signing as Alex Chen</span>}
            >
              <div className="px-4 py-4 text-center">
                <p className="text-sm font-medium">Thanks for signing</p>
                <div className="max-w-xs mx-auto">
                  <BrandedCompletionMessage branding={previewBranding} />
                </div>
              </div>
            </BrandedSigningFrame>
          </div>
          <p className="text-xs text-muted-foreground">What recipients see. Updates as you type.</p>
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-2 pt-4 border-t">
        {/* Two-step, and the quietest control here. It wipes branding for
              everything it applies to, and sat at the same visual weight as Save with no
              confirmation — one misclick from an unbranded organisation. Only
              offered when there is a saved brand to remove. */}
        {saved && (
          <Button
            variant="ghost"
            size="sm"
            className={confirmingClear ? 'text-destructive' : 'text-muted-foreground'}
            onClick={() => (confirmingClear ? clear.mutate() : setConfirmingClear(true))}
            disabled={clear.isPending || save.isPending}
          >
            {clear.isPending
              ? 'Removing…'
              : confirmingClear
                ? 'Click again to remove branding'
                : 'Remove branding'}
          </Button>
        )}
        <p className="ml-auto text-xs text-muted-foreground" aria-live="polite">
          {dirty
            ? changedElsewhere
              ? 'Unsaved changes — the brand was changed elsewhere since you started; saving replaces it'
              : 'Unsaved changes'
            : justSaved
              ? 'Saved'
              : ''}
        </p>
        <Button onClick={handleSave} disabled={!dirty || save.isPending || clear.isPending}>
          {save.isPending ? 'Saving…' : 'Save'}
        </Button>
      </div>
    </div>
  );
}
